import { Loader } from '@googlemaps/js-api-loader';
import { DateTime, Zone } from 'luxon';
import { capitalize, inject, Ref, toRaw } from 'vue';

import Environment from '@/common/enums/environment.enum';
import { HivestackApp } from '@/common/models/HivestackApp';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { LanguageLocaleKey } from '@/lang';
import formatNumberUtils from '@/utils/NumberFormatter';
import i18n from '@/vueI18n';

export const MapboxPublicToken =
  'pk.eyJ1IjoicHl0dHJvbGwiLCJhIjoiY2oxZGlrazNmMDBkODMyb2VqbGJxaTdyOSJ9.AqVow0wWbpVXnL0ZLO0ISw';
export const GoogleMapsApiKey = 'AIzaSyD_M9XegSLuDgaSCZrYZjxl7ZM9UMPucD8';
export const BaiduMapsApiKey = 'hMgKBBYn8bG31UoTH1gSlmooY6Idw9Cb';
export const HereAppID = 'MZUxVkrVlPrE0pm4aX9Y';
export const HereAppCode = 's5p6GlpHWWQuppsUIRDU7g';
export const serverDateFormat = 'yyyy-LL-dd';
export const HttpStatusCodeNoResponse = 450;
export const ICPLicense = '沪ICP备2021014258号-1';
export const DefaultUserProfilePicture =
  'https://hs-profile-pictures-staging.s3.amazonaws.com/7c2473cc-f8a6-11ec-b939-0242ac120002.png';

let appInstance: HivestackApp = null;

export const getAppInstance = (): HivestackApp => appInstance;

export const setAppInstance = (app: HivestackApp) => {
  appInstance = app;
};

export function getLanguage(): LanguageLocaleKey {
  return getAppInstance()?.config.globalProperties.lang || LanguageLocaleKey.EN_US;
}

export function setLanguage(language: LanguageLocaleKey) {
  getAppInstance().config.globalProperties.lang = language;
}

let rootStore = null;

export const getStore = () => rootStore;

export const setStore = (store) => {
  rootStore = store;
};

//
// General utils
//
export const DASH = '—';
export const getValueOrDash = (value: string): string => value ?? DASH;

export const generateUUID = () => {
  let uuid = '';
  let random = null;
  for (let i = 0; i < 32; i += 1) {
    random = (Math.random() * 16) | 0;

    if (i === 8 || i === 12 || i === 16 || i === 20) {
      uuid += '-';
    }
    uuid += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(16);
  }
  return uuid;
};

export const getContainerWidth = (selector) => {
  const container = window.document.querySelector(selector);
  return container == null ? 0 : container.clientWidth;
};

export const getContainerHeight = (selector) => {
  const container = window.document.querySelector(selector);
  return container == null ? 0 : container.clientHeight;
};

export const isInViewport = (container, elem) => {
  const bounding = elem.getBoundingClientRect();
  const containerBounding = container.getBoundingClientRect();
  return (
    bounding.top >= containerBounding.top &&
    bounding.bottom <= window.innerHeight &&
    bounding.right <= containerBounding.right &&
    bounding.left >= containerBounding.left
  );
};

export const isBoolean = (arg) => arg === !!arg;
export const isString = (arg) => typeof arg === 'string';
export const isNumeric = (str) => {
  if (typeof str !== 'string') return false; // we only process strings!
  return (
    !Number.isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !Number.isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
};

export const without = (arr, ...args) => arr.filter((item) => !args.includes(item));

export const has = (obj, path) => {
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : `${path}`.match(/([^[.\]])+/g);
  return !!pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj);
};

export const setProp = (obj, path, value) => {
  // Regex explained: https://regexr.com/58j0k
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);

  pathArray.reduce((acc, key, i) => {
    if (acc[key] == null) acc[key] = {};
    if (i === pathArray.length - 1) acc[key] = value;
    return acc[key];
  }, obj);
};

export const camelToSnakeCase = (text) =>
  text
    .replace(/(.)([A-Z][a-z]+)/, '$1_$2')
    .replace(/([a-z0-9])([A-Z])/, '$1_$2')
    .toLowerCase();
export const snakeToCamel = (text) => text.replace(/([-_]\w)/g, (g) => g[1].toUpperCase());
export const sentenceToCamel = (text) =>
  text.replace('&', '').replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

export const debounce = (callback, wait, timeout) => {
  return (...args) => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      callback.apply(this, args);
    }, wait);
  };
};

export function copyValue(value) {
  if (value == null) {
    return null;
  }
  switch (typeof value) {
    case 'object':
      return JSON.parse(JSON.stringify(value));
    default:
      return value;
  }
}

//
// Array utils
//
export const areArraysEqual = (a, b) => {
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  if (a.length === b.length && a.length === 0) {
    return true;
  }
  const aSorted = [...a].sort((x, y) => x - y);
  const bSorted = [...b].sort((x, y) => x - y);

  if (typeof aSorted[0] !== typeof bSorted[0]) {
    return false;
  }

  return aSorted
    .map((val, i) =>
      typeof val === 'object' ? JSON.stringify(val) === JSON.stringify(bSorted[i]) : bSorted[i] === val,
    )
    .every((isSame) => isSame);
};

export const areArraysIdentical = (a, b) => {
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  return a.map((val, i) => b[i] === val).every((isSame) => isSame);
};

//
// Object utils
//
export const areObjectsEqual = (obj1, obj2) => {
  // Check if both arguments are objects
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return false;
  }

  // Get the keys of both objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if the number of keys is different
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Check if values of each key are the same
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};
export function objectOmitted<T extends object, K extends keyof T>(obj: T, keys: K[]) {
  const result = {} as Omit<T, K>;
  Object.keys(obj).forEach((k) => {
    if (!keys.includes(k as K)) {
      result[k] = obj[k as keyof T];
    }
  });
  return result;
}
// Returns the changed keys with the value of the second, newObj
export function getObjectDifferences<T extends object>(oldObj: T, newObj: T): Partial<T> {
  const diff: Partial<T> = {};
  if (!oldObj || !newObj) return diff;
  Object.keys(oldObj).forEach((k) => {
    if (Object.hasOwn(oldObj, k) && Object.hasOwn(newObj, k)) {
      const v1 = toRaw(oldObj[k]);
      const v2 = toRaw(newObj[k]);
      if (Array.isArray(v1) && Array.isArray(v2)) {
        if (!areArraysEqual(v1, v2)) {
          diff[k] = v2;
        }
      } else if (typeof v1 === 'object' && typeof v2 === 'object') {
        const differences = getObjectDifferences(v1, v2);
        if (!isObjectEmpty(differences)) {
          diff[k] = v2;
        }
      } else if (v1 !== v2) {
        diff[k] = v2;
      }
    }
  });
  return diff;
}
export const isObjectEmpty = (obj) => Object.keys(obj).length === 0;

//
// Date utils
//
export const toServerDate = (date) => {
  return date === null || date === undefined ? null : `${DateTime.fromISO(date).toFormat(serverDateFormat)}`;
};

export const toServerDateTime = (date?: DateTime | string): string | null => {
  if (date == null) {
    return null;
  }
  if (date instanceof DateTime) {
    return date.toUTC().toISO({ includeOffset: false, suppressMilliseconds: true });
  }
  return `${DateTime.fromISO(date).toUTC().toISO({ includeOffset: false, suppressMilliseconds: true })}`;
};

export const toServerDateTimeNoTimezone = (date?: DateTime | string, suppressMilliseconds = false): string | null => {
  if (date == null) {
    return null;
  }
  if (date instanceof DateTime) {
    return date.toLocal().toISO({ includeOffset: false, suppressMilliseconds });
  }
  return `${DateTime.fromISO(date).toLocal().toISO({ includeOffset: false, suppressMilliseconds })}`;
};

export const fromServerDate = (date: string): DateTime => (date == null ? null : DateTime.fromISO(date));

export const fromServerUtcDate = (date: string): DateTime =>
  date === null || date === undefined ? null : DateTime.fromISO(date, { zone: 'utc' });

export const fromServerDateTime = (date: string): DateTime =>
  date == null ? null : DateTime.fromISO(date.indexOf('+') === -1 ? date : date.split('+')[0]);

export const fromServerTzDate = (date: string, zone: string | Zone) => {
  return date == null ? null : DateTime.fromISO(date, { zone });
};

export const getDateFormatForLocale = (locale: string): string => {
  let format = 'dd/LL/yyyy';
  if (locale === 'en') {
    format = 'LL/dd/yyyy';
  }
  return format;
};

//
// Geo utils
//
export const deg2rad = (deg: number): number => deg * (Math.PI / 180);

export const getDistanceFromLatLonInKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  // Distance in km
  return R * c;
};

export const isPointInsidePolygon = (point: [number, number], vs: [number, number][]) => {
  const [x, y] = point;

  let inside = false;
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const [xi, yi] = vs[i];
    const [xj, yj] = vs[j];

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
};

export const getBoundsZoomLevel = (
  bounds: google.maps.LatLngBounds,
  mapDim: { width: number; height: number },
): number => {
  const WORLD_DIM = {
    height: 256,
    width: 256,
  };
  const ZOOM_MAX = 21;

  const latRad = (lat) => {
    const sin = Math.sin(lat * (Math.PI / 180));
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  };

  const zoom = (mapPx, worldPx, fraction) => {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  };

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
};

export const flattenGeoJsonGeometry = (geometries) =>
  geometries
    .filter((g) => g != null)
    .map((g) => {
      let { coordinates } = g;
      while (typeof coordinates[0][0] !== 'number') {
        coordinates = coordinates.reduce((mem, x) => [...mem, ...x], []);
      }
      return coordinates;
    })
    .reduce((mem, x) => [...mem, ...x], []);

export const getBounds = (coordinates: number[][]): number[][] | null => {
  if (coordinates == null || coordinates.length === 0) {
    return null;
  }
  //
  // We cannot use Math.min / Math.max here because it will cause
  // a "Maximum call stack size exceeded"
  //
  // const minLat = Math.min(...coordinates.map((x) => x[1]));
  // const maxLat = Math.max(...coordinates.map((x) => x[1]));
  // const minLng = Math.min(...coordinates.map((x) => x[0]));
  // const maxLng = Math.max(...coordinates.map((x) => x[0]));

  const minLat = coordinates.reduce((lat, x) => (x[1] < lat ? x[1] : lat), 90);
  const maxLat = coordinates.reduce((lat, x) => (x[1] > lat ? x[1] : lat), -90);
  const minLng = coordinates.reduce((lng, x) => (x[0] < lng ? x[0] : lng), 180);
  const maxLng = coordinates.reduce((lng, x) => (x[0] > lng ? x[0] : lng), -180);

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

//
// Daypart utils
//
export const daypartLength = 7 * 24;
export const preciseDaypartLength = 7 * 24 * 60;

export const FullDaypart =
  '111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111111';
export const EmptyDaypart = Array(daypartLength).fill(0).join('');
export const FullPreciseDaypart = Array(preciseDaypartLength).fill(1).join('');
export const EmptyPreciseDaypart = Array(preciseDaypartLength).fill(0).join('');
export const InvalidDaypart = '<invalid>';

export function isPreciseDayPart(daypart: string): boolean {
  return daypart.length === preciseDaypartLength;
}

export const isDaypartEmpty = (daypart: string): boolean => {
  return daypart === null || daypart.indexOf('1') === -1;
};

export const isDaypartFull = (daypart: string): boolean => {
  return daypart !== null && daypart.indexOf('0') === -1;
};

export const isValidDaypart = (daypart: string): boolean => {
  return (
    daypart !== null && daypart.length === 168 && Array.from(daypart).filter((c) => c !== '1' && c !== '0').length === 0
  );
};

/**
 * @param {string} daypart
 * @returns {string}
 */
export function convertPreciseToStandardDaypart(daypart: string): string | false {
  if (!isPreciseDayPart(daypart)) {
    return false;
  }
  let output = '';
  const minutesInHours = 60;
  const minutesInDay = 24 * 60;
  for (let day = 0; day < 7; day += 1) {
    const daySlice = daypart.slice(minutesInDay * day, minutesInDay * (day + 1));
    for (let hour = 0; hour < 24; hour += 1) {
      const hourSlice = daySlice.slice(minutesInHours * hour, minutesInHours * (hour + 1));
      const isHourEmpty = hourSlice.indexOf('1') === -1;
      const isHourFull = hourSlice.indexOf('0') === -1;
      if (isHourEmpty) {
        output += '0';
      } else if (isHourFull) {
        output += '1';
      } else {
        throw Error(
          'Cannot convert precise daypart to standard daypart - Precise daypart contains hours that are not full or empty',
        );
      }
    }
  }
  return output;
}

export function convertStandardDaypartToPrecise(daypart: string): string {
  if (!isValidDaypart(daypart)) {
    throw Error('Invalid daypart');
  }

  let output = '';
  const minutesInHours = 60;

  daypart.split('').forEach((d) => {
    if (d === '0' || d === '1') {
      output += d.repeat(minutesInHours);
    } else {
      output += d;
    }
  });

  return output;
}

export const getDaypartDescription = (daypart: string, asHtml = true): string => {
  if (!isValidDaypart(daypart)) {
    return i18n.global.t('messages.invalidDaypart');
  }
  if (isDaypartFull(daypart)) {
    return i18n.global.t('messages.allHoursOfTheDayAllWeek');
  }
  if (isDaypartEmpty(daypart)) {
    return i18n.global.t('messages.noSelection');
  }

  const operatingHoursDescription = (ranges) => {
    if (ranges.length === 1) {
      return ranges.join('');
    }
    return asHtml ? `<div>${ranges.join('</div><div>')}</div>` : ranges.join(', ');
  };

  const daysInfo = [];
  for (let d = 0; d < 7; d += 1) {
    let startHour = null;
    for (let h = 0; h <= 24; h += 1) {
      if (daypart[d * 24 + h] === '1' && h !== 24) {
        if (startHour === null) {
          startHour = h;
        }
      } else if (startHour !== null) {
        daysInfo.push({
          name: DateTime.local().startOf('week').plus({ days: d }).toLocaleString({ weekday: 'short' }),
          startHour,
          endHour: h,
          hasHours: h - startHour !== 24,
          day: d,
        });
        startHour = null;
      }
    }
  }

  // Check the complexity of the schedule.
  const groupByHours = daysInfo.reduce((mem, x) => {
    const key = `${DateTime.local()
      .startOf('day')
      .plus({ hours: x.startHour })
      .toLocaleString(DateTime.TIME_SIMPLE)
      .replace(':00 ', '')}–${DateTime.local()
      .startOf('day')
      .plus({ hours: x.endHour })
      .toLocaleString(DateTime.TIME_SIMPLE)
      .replace(':00 ', '')}`;
    const mutatedMem = { ...mem };
    if (mem[key]) {
      mutatedMem[key].push(x);
    } else {
      mutatedMem[key] = [x];
    }
    return mutatedMem;
  }, {});

  const ranges = Object.keys(groupByHours).map((x) => {
    const groupedDaysInfo = groupByHours[x];
    const operatingHour = (name, hasHours, hours) => `${name} ${hasHours ? hours : ''}`.trim();
    const range = [];
    let mem = '';
    groupedDaysInfo.forEach((groupDay, i) => {
      if (i === groupedDaysInfo.length - 1 || groupedDaysInfo[i + 1].day - groupDay.day !== 1) {
        if (mem === '') {
          range.push(operatingHour(groupDay.name, groupDay.hasHours, x));
        } else {
          range.push(operatingHour(`${mem}-${groupDay.name}`, groupDay.hasHours, x));
        }
        mem = '';
      } else if (mem === '') {
        mem = groupDay.name;
      }
    });
    return operatingHoursDescription(range);
  });

  return operatingHoursDescription(ranges);
};

//
// Temperature conversion utils
//
export const celsiusToFahrenheit = (t) => t * (9 / 5) + 32;

export const fahrenheitToCelsius = (t) => (t - 32) * (5 / 9);

//
// Distance conversion utils
//
export const centimetersToInches = (d, decimals = 1) => Math.round(10 ** decimals * (d / 2.54)) / 10 ** decimals;

export const inchesToCentimeters = (d, decimals = 0) => Math.round(10 ** decimals * d * 2.54) / 10 ** decimals;

export const millimetersToInches = (d, decimals = 1) => Math.round(10 ** decimals * (d / 25.4)) / 10 ** decimals;

export const inchesToMillimeters = (d, decimals = 0) => Math.round(10 ** decimals * d * 25.4) / 10 ** decimals;

//
// URL validation
//
export const isValidURL = (value: string): boolean => {
  if (value !== undefined && value !== null) {
    const pattern = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[;-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[|;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$',
      'i',
    ); // fragment locator
    return !!pattern.test(value);
  }
  return true;
};

export const isValidTokenizedURL = (value: string): boolean => {
  // Allow token in the form of ${TOKEN} in the url
  if (value !== undefined && value !== null) {
    const pattern = new RegExp('(\\b(https?):\\/\\/)?[-A-Za-z0-9+&@#/%?=~_|!:,.;${}]+[-A-Za-z0-9+&@#/%=~_|${}]', 'i');
    return !!pattern.test(value);
  }
  return true;
};

//
// Numbers
//
export const roundToSixDecimals = (n: number) => Math.round(100000 * n) / 100000;
export const roundToTwoDecimals = (n: number) => Math.round(10 * n) / 10;

export function round(value: number, decimals: number) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

//
// Strings
//
export const normalizeString = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const sentenceCase = (s: string): string => {
  return !s || s.length === 0
    ? ''
    : s.includes('. ')
      ? s
          .split('. ')
          .map((x) => capitalize(x))
          .join('. ')
      : capitalize(s);
};

export const formatNumber = formatNumberUtils;

export const formatCurrency = (
  value: number = 0,
  options?: { currencyCode?: string; includeSymbol?: boolean; showCurrencyCode?: boolean; isUsedAsLabel?: boolean },
) => {
  const { currencyCode, includeSymbol = true, showCurrencyCode = false, isUsedAsLabel = false } = options || {};
  const locale = i18n.global.locale;
  let currencySymbol = '';
  if (includeSymbol === false) {
    return formatNumber(value, 'currencyWithoutSymbol');
  }
  if (currencyCode == null || locale == null) {
    return i18n.global.n(value, 'currency');
  }
  const intlCurrencyFormat = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
  })
    .formatToParts(value)
    .map((part) => {
      if (part.type === 'currency') {
        /*
         * Sometimes the currency will display as US$ 1.00 or CA$ 1.00 instead of $1.00
         * Since we want to always display the currency symbol before the value, we need to remove the country code for these cases
         */
        currencySymbol = part.value.replace(/[A-Za-z]{1,3}\$/g, '$');
        return showCurrencyCode ? `${currencyCode}${currencySymbol} ` : currencySymbol;
      }
      return part.value;
    })
    .join('')
    .trim();
  const formattedCurrency = isUsedAsLabel
    ? intlCurrencyFormat
        .split(' ')
        .map((x) => {
          const value = x.replace(/[^0-9.]/g, '');
          const formattedValue =
            Number(value) > 0 && Number(value) < 100 ? formatNumber(value, '0,0.00') : formatNumber(value, '0.0a');
          return `${currencySymbol}${formattedValue}`;
        })
        .join('')
        .trim()
    : intlCurrencyFormat;
  return formattedCurrency;
};

export const uppercase = (s: string): string => (!s ? '' : s.toUpperCase());

export const titlecase = (s: string): string =>
  !s
    ? ''
    : s
        .toLowerCase()
        .split(' ')
        .map((x) => x.charAt(0).toUpperCase() + x.substring(1))
        .join(' ');

//
// Clipboard
//
export function copyToClipboard(text: string, successCallback?: () => void, failureCallback?: () => void) {
  const handleResult = (isSuccess: boolean) => {
    if (isSuccess && successCallback != null) {
      successCallback();
    } else if (!isSuccess && failureCallback != null) {
      failureCallback();
    }
    return handleResult;
  };

  if (!navigator.clipboard) {
    handleResult(false);
    return;
  }

  navigator.clipboard
    .writeText(text)
    .then(() => handleResult(true))
    .catch(() => handleResult(false));
}

export const toKebabCase = (str: string): string =>
  str
    .replace(/([a-z])([A-Z])/g, '$1-$2') // get all lowercase letters that are near to uppercase ones
    .replace(/[\s_]+/g, '-') // replace all spaces and low dash
    .toLowerCase();

//
// Environment
//
let env: string = Environment.PROD;
if (window.location.host.indexOf('staging') > -1) {
  env = Environment.STAGING;
} else if (window.location.host.indexOf('uat') > -1) {
  env = Environment.UAT;
} else if (window.location.host.indexOf('localhost') > -1 || window.location.host.indexOf('127.0.0.1') > -1) {
  env = Environment.DEV;
}

export const environment: string = env;

export const isWindowsOS = (): boolean => window.navigator.userAgent.includes('Win');

//
// Regions
//
export const Regions = {
  China: 'china',
  Japan: 'japan',
  NA: 'NA',
  RestOfTheWorld: 'rest-of-the-world',
};

let reg: string = Regions.RestOfTheWorld;
if (window.location.host.indexOf('.cn') > -1) {
  reg = Regions.China;
} else if (window.location.host.indexOf('.jp') > -1 || window.location.host.includes('jpn-')) {
  reg = Regions.Japan;
} else if (window.location.host.indexOf('.com') > -1) {
  reg = Regions.NA;
}
export const region: string = reg;

export const isNotChina = () => region !== Regions.China;

//
// Blobs
//
export const readFileAsDataURL = async (file: File) =>
  new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = () => resolve(fileReader.result);
    fileReader.readAsDataURL(file);
  });

//
// CSS Size
//
export const convertToPixels = (size: string): number => {
  if (size.includes('rem')) {
    const sizeAsNumber = Number.parseFloat(size.replace('rem', ''));
    return sizeAsNumber * parseFloat(getComputedStyle(window.document.documentElement).fontSize);
  }
  if (size.includes('px')) {
    return Number.parseFloat(size.replace('px', ''));
  }
  return Number.parseFloat(size);
};

export function first<T>(list: Array<T>): T {
  return list[0];
}

export function last<T>(list: Array<T>): T {
  return list[list.length - 1];
}

export function sortBy<T>(key: string): (a: T, b: T) => number {
  return (a: T, b: T) => {
    if (typeof a[key] === 'string') {
      return a[key].localeCompare(b[key]);
    }

    return a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0;
  };
}

export function orderBy<T>(list: T[], key?: string): T[] {
  if (!list?.length) return list;
  return key ? list.sort(sortBy(key)) : list.sort();
}

export function uniq<T>(list: T[]): T[] {
  return [...new Set<T>(list)];
}

export function uniqBy<T>(list: T[], key: string): T[] {
  const uniqKeyList = uniq(list.map((l) => l[key]));
  const newList = [];

  uniqKeyList.forEach((k) => {
    const el = list.find((l) => k === l[key]);
    newList.push(el);
  });

  return newList;
}

export function groupBy<T>(list: T[], key: string): Record<string, T[]> {
  return list.reduce((a, b) => {
    a[b[key]] = a[b[key]] || [];
    a[b[key]].push(b);
    return a;
  }, {});
}

//
// Google API
//
declare global {
  interface Window {
    google: { maps: typeof google.maps };
    initGooglePlacesApi: () => void;
    initGoogleDirectionsApi: () => void;
  }
}

let loader: Loader = null;
export async function loadGooglePlacesApi() {
  if (!loader) {
    loader = new Loader({ apiKey: GoogleMapsApiKey, version: 'weekly', libraries: ['visualization'] });
    await loader.load();
  }
  await loader.importLibrary('places');
}

export async function loadGoogleDirectionsApi() {
  if (!loader) {
    loader = new Loader({ apiKey: GoogleMapsApiKey, version: 'weekly', libraries: ['visualization'] });
    await loader.load();
  }
  await loader.importLibrary('routes');
}

export function isNil<T>(value: T): boolean {
  return value == null;
}

export function findPath(object, key: string, value): string {
  const path = [];
  const keyExists = (obj): boolean => {
    if (!obj || (typeof obj !== 'object' && !Array.isArray(obj))) {
      return false;
    }

    if (obj?.[key] === value) {
      return true;
    }

    if (Array.isArray(obj)) {
      const parentKey = path.length ? path.pop() : '';

      for (let i = 0; i < obj.length; i += 1) {
        path.push(`${parentKey}[${i}]`);
        const result = keyExists(obj[i]);
        if (result) {
          return result;
        }
        path.pop();
      }
    } else {
      for (const k in obj) {
        path.push(k);
        const result = keyExists(obj[k]);
        if (result) {
          return result;
        }
        path.pop();
      }
    }

    return false;
  };

  keyExists(object);

  return path.join('.');
}

export function resizeImage(src: string, maxWidth = 512, maxHeight = 512, backgroundColor = null): Promise<string> {
  return new Promise<string>((resolve) => {
    const image = new window.Image();
    image.addEventListener('load', () => {
      const canvas = document.createElement('canvas');
      let { width, height } = image;
      if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
      } else if (width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);
      }
      ctx.drawImage(image, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl);
    });
    image.src = src;
  });
}

export function i(key, count = 1) {
  return i18n.global.t(key, { n: count });
}

export function getHours() {
  const hours = [];
  const useAMPM =
    DateTime.local().toLocaleString(DateTime.TIME_SIMPLE).indexOf('AM') !== -1 ||
    DateTime.local().toLocaleString(DateTime.TIME_SIMPLE).indexOf('PM') !== -1;
  const format = useAMPM ? 'ha' : 'HH';
  for (let h = 0; h < 24; h += 1) {
    const date = DateTime.local().startOf('day').plus({ hours: h });
    hours.push(date.toFormat(format));
  }
  return hours;
}

export function capitalizeTranslation(key: string, args: string[] = []): string {
  return capitalize(i18n.global.t(key, args));
}

export function clearTooltips() {
  GlobalEventBus.$emit(GlobalEvents.clearTooltips);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function useInjected<T>(key: string) {
  const injectedValue: Ref<T> = inject(key);
  if (!injectedValue) {
    throw new Error(`Injection with key "${key}" not found.`);
  }
  return injectedValue;
}
